import { ResponseWithResults } from '../interfaces/online-only.interface';

function isResponseWithResults<T>(obj: ResponseWithResults<T> | T): obj is ResponseWithResults<T> {
    const results = (<ResponseWithResults<T>>obj).results;

    return results && Array.isArray(results);
}

export class ODataUtilities {
    /**
     * Removes the first character of and endpoint if it is a '/'. The backend needs the endpoint without the '/'.
     *
     * @param endpoint The URL of the endpoint
     * @returns {string} the endpoint without leading '/'
     */
    public static removeLeadingSlash(endpoint: string): string {
        if (endpoint.charAt(0) === '/') {
            return endpoint.substr(1);
        } else {
            return endpoint;
        }
    }

    /**
     * Transforms the payload of a http response object into an array even if it's a single object.
     *
     * @param responseData http response object including statusCode code, data and more...
     * @returns {Array} always an array even if the responseData is a single object
     */
    public static transformResponseDataToArray<T>(responseData: ResponseWithResults<T> | T): T[] {
        return isResponseWithResults(responseData) ? responseData.results : [responseData];
    }

    /**
     * This function accepts an object containing one string property that with an array of strings as value.
     * Additionally it accepts an array of strings.
     * By default the array of the object is concatenated with the additional, after that the new array is joined with a ','.
     * If the insertAtFront is true, the additional needs to be in the zero index position concatenated with array of the object(reverse way).
     * A new object is created that has one property of the same name as the original object and the value is set to
     * the newly joined string.
     *
     * Examples: { foo: [ "one", "two" ]} => { foo: "one,two" }
     * { foo: [ "one", "two" ]}, [ "three", "four" ] => { foo: "one,two,three,four" }
     *
     * In theory the function also supports objects with more than one property, but our use case is only for simple
     * outmodel objects with one property. Unfortunately TypeScript does not allow a type definition of an object with
     * only one property of unknown name, so this will have to do.
     *
     * @param toAppend
     * @param insertAtFront
     */
    public static joinAndAppendArrayOutModel(
        outModel: { [key: string]: string[] },
        toAppend: string[] = [],
        insertAtFront?: boolean,
    ): { [key: string]: string } {
        const returnObject: { [key: string]: string } = {};

        for (const property of Object.keys(outModel)) {
            if (insertAtFront) {
                outModel[property] = toAppend.concat(outModel[property]);
            } else {
                outModel[property] = outModel[property].concat(toAppend);
            }
            returnObject[property] = outModel[property].join(',');
        }

        return returnObject;
    }

    /**
     * This function behaves same as above except that it accepts an object containing one string property with not an array of strings as value.
     * Additionally it accepts an array of strings.
     * By default the array of strings is joined with ',' and converted to string object and concatenated with the outmodel object value.
     * If the insertAtFront is true, the additional needs to be in the zero index position concatenated with the object(reverse way).
     * A new object is created that has one property of the same name as the original object and the value is set to
     * the newly joined string.
     *
     * Examples: { foo: "one"}, ["two"] => { foo: "one,two" }
     * { foo: "one"}, ["two", "three"] => { foo: "one,two,three" }
     *
     * In theory the function also supports objects with more than one property, but our use case is only for simple
     * outmodel objects with one property. Unfortunately TypeScript does not allow a type definition of an object with
     * only one property of unknown name, so this will have to do.
     *
     * @param toAppend
     * @param insertAtFront
     */
    public static joinAndAppendObjectOutModel(
        outModel: { [key: string]: string },
        toAppend: string[] = [],
        insertAtFront?: boolean,
    ): { [key: string]: string } {
        const returnObject: { [key: string]: string } = {};

        for (const property of Object.keys(outModel)) {
            if (insertAtFront) {
                outModel[property] = toAppend.join(',').concat(',').concat(outModel[property]);
            } else {
                outModel[property] = outModel[property].concat(',').concat(toAppend.join(','));
            }
            returnObject[property] = outModel[property];
        }
        return returnObject;
    }
}
